import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Loader } from '../../components/1elements';
import AuthorizationError from '../../components/2compounds/AuthorizationError';
import DashboardCsvImport from './DashboardCsvImport';
import DashboardApproverField from './DashboardApproverField';
import DashboardValueManager from './DashboardValueManager';
import {
  getDashboardErrorMessage,
  useCreateDashboardSubmissionMutation,
  useGetEffectiveDashboardQuery
} from './dashboardApi';
import {
  DASHBOARD_APPROVAL_MODE,
  DASHBOARD_DATA_STATE,
  DASHBOARD_METRIC_VALUE_STATUS,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';
import {
  canImportDashboardData,
  canUpdateDashboardData
} from './dashboardPermissions';
import {
  buildDashboardSubmissionMessage,
  getActiveDashboardMetrics,
  getDashboardMetricConstraintText,
  getDashboardMetricStep,
  getDashboardValueStatusLabel,
  validateDashboardSubmission
} from './dashboardSubmissionModel';
import { formatDashboardMetricValue } from './dashboardViewModel';

const statusColour = (status) => {
  if (status === DASHBOARD_METRIC_VALUE_STATUS.APPROVED) return 'success';
  if (status === DASHBOARD_METRIC_VALUE_STATUS.PENDING_APPROVAL)
    return 'warning';
  if (status === DASHBOARD_METRIC_VALUE_STATUS.REJECTED) return 'error';
  return 'default';
};

const formatSubmittedValue = (metric, value) => {
  if (!metric) return String(value);

  return formatDashboardMetricValue(metric, {
    dataState: DASHBOARD_DATA_STATE.AVAILABLE,
    value
  });
};

const DashboardSubmissionConfirmation = ({ metrics, submission }) => {
  const metricById = new Map(
    metrics.map((metric) => [metric.metricDefinitionId, metric])
  );
  const hasPendingApproval = (submission.summary?.pendingApproval ?? 0) > 0;

  return (
    <Box data-testid="dashboard-submission-confirmation">
      <Alert severity={hasPendingApproval ? 'info' : 'success'}>
        <AlertTitle>Dashboard data submitted</AlertTitle>
        {buildDashboardSubmissionMessage(submission.summary)}
      </Alert>
      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', mt: 2, p: 2 }}
      >
        <Typography fontWeight={700} sx={{ mb: 1.5 }}>
          Submission results
        </Typography>
        <Stack
          divider={
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
          }
        >
          {(submission.values ?? []).map((item) => (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              key={item.metricValueRevisionId ?? item.metricDefinitionId}
              spacing={2}
              sx={{ py: 1 }}
            >
              <Box>
                <Typography fontWeight={600}>{item.label}</Typography>
                <Typography color="text.secondary" variant="body2">
                  {formatSubmittedValue(
                    metricById.get(item.metricDefinitionId),
                    item.value
                  )}
                </Typography>
              </Box>
              <Chip
                color={statusColour(item.valueStatus)}
                label={getDashboardValueStatusLabel(item.valueStatus)}
                size="small"
                variant="outlined"
              />
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

const DashboardDataEntry = ({ locationId }) => {
  const canUpdate = canUpdateDashboardData();
  const canImport = canImportDashboardData();
  const today = dayjs().format('YYYY-MM-DD');
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [fieldValues, setFieldValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [effectiveDateError, setEffectiveDateError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [approver, setApprover] = useState(null);
  const [approverError, setApproverError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const effectiveQuery = useGetEffectiveDashboardQuery(locationId, {
    skip: !locationId || !canUpdate
  });
  const [createSubmission, createState] =
    useCreateDashboardSubmissionMutation();
  const metrics = useMemo(
    () => getActiveDashboardMetrics(effectiveQuery.data?.metrics),
    [effectiveQuery.data?.metrics]
  );

  useEffect(() => {
    setEffectiveDate(today);
    setFieldValues({});
    setFieldErrors({});
    setEffectiveDateError(null);
    setFormError(null);
    setApprover(null);
    setApproverError(null);
    setSubmission(null);
  }, [effectiveQuery.data?.dashboardVersionId, locationId, today]);

  const handleMetricChange = (metricDefinitionId, value) => {
    setFieldValues((current) => ({
      ...current,
      [metricDefinitionId]: value
    }));
    setFieldErrors((current) => {
      if (!current[metricDefinitionId]) return current;
      const next = { ...current };
      delete next[metricDefinitionId];
      return next;
    });
    setFormError(null);
    setApproverError(null);
    setSubmission(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmission(null);

    const validation = validateDashboardSubmission({
      effectiveDate,
      fieldValues,
      metrics,
      maximumEffectiveDate: today
    });
    setEffectiveDateError(validation.effectiveDateError);
    setFieldErrors(validation.fieldErrors);
    setFormError(validation.formError);
    if (!validation.isValid) return;

    const requiresApproval = validation.values.some(({ metricDefinitionId }) =>
      metrics.some(
        (metric) =>
          metric.metricDefinitionId === metricDefinitionId &&
          metric.approvalMode === DASHBOARD_APPROVAL_MODE.REQUIRED
      )
    );
    if (requiresApproval && !approver?.user_id) {
      setApproverError('Select who should receive the approval email.');
      return;
    }

    try {
      const created = await createSubmission({
        locationId,
        effectiveDate,
        values: validation.values,
        approverUserId: requiresApproval ? approver.user_id : null
      }).unwrap();
      setSubmission(created);
      setFieldValues({});
      setFieldErrors({});
      setFormError(null);
      setApprover(null);
      setApproverError(null);
    } catch (_error) {
      // RTK Query exposes the normalized error through createState below.
    }
  };

  if (!canUpdate) return <AuthorizationError />;
  if (!locationId) {
    return (
      <Alert severity="error">
        A location is required to enter dashboard data.
      </Alert>
    );
  }
  if (effectiveQuery.isLoading) return <Loader />;
  if (effectiveQuery.error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {getDashboardErrorMessage(effectiveQuery.error)}
      </Alert>
    );
  }
  if (!effectiveQuery.data) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        No effective dashboard is available for data entry.
      </Alert>
    );
  }

  return (
    <Box
      data-testid="dynamic-dashboard-data-entry"
      sx={{
        height: '100%',
        overflowY: 'auto',
        p: { xs: 2, md: 3 },
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Typography
          component="h1"
          fontSize={{ xs: 24, md: 30 }}
          fontWeight={700}
        >
          Enter dashboard data
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Add values to {effectiveQuery.data.name}. Required values must be
          included; optional values may be left blank.
        </Typography>

        {canImport && metrics.length > 0 && (
          <DashboardCsvImport
            dashboardVersionId={effectiveQuery.data.dashboardVersionId}
            locationId={locationId}
            onImported={() => setSubmission(null)}
          />
        )}

        {submission && (
          <Box sx={{ mt: 3 }}>
            <DashboardSubmissionConfirmation
              metrics={metrics}
              submission={submission}
            />
          </Box>
        )}

        {metrics.length === 0 ? (
          <Alert severity="info" sx={{ mt: 3 }}>
            This dashboard has no active metrics available for data entry.
          </Alert>
        ) : (
          <Paper
            component="form"
            elevation={0}
            noValidate
            onSubmit={handleSubmit}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              mt: 3,
              p: { xs: 2, md: 3 }
            }}
          >
            <TextField
              InputLabelProps={{ shrink: true }}
              error={Boolean(effectiveDateError)}
              helperText={
                effectiveDateError || 'The date these values apply to.'
              }
              inputProps={{ max: today }}
              label="Effective date"
              onChange={(event) => {
                setEffectiveDate(event.target.value);
                setEffectiveDateError(null);
                setFormError(null);
                setSubmission(null);
              }}
              required
              size="small"
              type="date"
              value={effectiveDate}
            />

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))'
                },
                mt: 3
              }}
            >
              {metrics.map((metric) => {
                const unit =
                  metric.unitLabel ||
                  (metric.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE
                    ? '%'
                    : null);

                return (
                  <Paper
                    elevation={0}
                    key={metric.metricDefinitionId}
                    sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}
                  >
                    <Stack
                      alignItems="flex-start"
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box>
                        <Typography fontWeight={700}>{metric.label}</Typography>
                        {metric.description && (
                          <Typography color="text.secondary" variant="body2">
                            {metric.description}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        color={
                          metric.approvalMode ===
                          DASHBOARD_APPROVAL_MODE.REQUIRED
                            ? 'warning'
                            : 'default'
                        }
                        label={
                          metric.approvalMode ===
                          DASHBOARD_APPROVAL_MODE.REQUIRED
                            ? 'Approval required'
                            : 'Immediate approval'
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <TextField
                      error={Boolean(fieldErrors[metric.metricDefinitionId])}
                      fullWidth
                      helperText={
                        fieldErrors[metric.metricDefinitionId] ||
                        getDashboardMetricConstraintText(metric)
                      }
                      InputProps={
                        unit
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {unit}
                                </InputAdornment>
                              )
                            }
                          : undefined
                      }
                      inputProps={{
                        max: metric.maximum ?? undefined,
                        min: metric.minimum ?? undefined,
                        step: getDashboardMetricStep(metric)
                      }}
                      label={`${metric.label} value`}
                      onChange={(event) =>
                        handleMetricChange(
                          metric.metricDefinitionId,
                          event.target.value
                        )
                      }
                      required={metric.isRequired}
                      size="small"
                      sx={{ mt: 2 }}
                      type="number"
                      value={fieldValues[metric.metricDefinitionId] ?? ''}
                    />
                  </Paper>
                );
              })}
            </Box>

            {formError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}

            {metrics.some(
              (metric) =>
                metric.approvalMode === DASHBOARD_APPROVAL_MODE.REQUIRED
            ) && (
              <Box sx={{ mt: 3, maxWidth: 520 }}>
                <DashboardApproverField
                  approver={approver}
                  error={approverError}
                  locationId={locationId}
                  onChange={(value) => {
                    setApprover(value);
                    setApproverError(null);
                  }}
                />
              </Box>
            )}
            {createState.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {getDashboardErrorMessage(
                  createState.error,
                  'Dashboard data could not be submitted.'
                )}
              </Alert>
            )}

            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Button
                disabled={createState.isLoading}
                type="submit"
                variant="contained"
              >
                {createState.isLoading
                  ? 'Submitting…'
                  : 'Submit dashboard data'}
              </Button>
              {createState.isLoading && <CircularProgress size={22} />}
            </Stack>
          </Paper>
        )}

        {metrics.length > 0 && (
          <DashboardValueManager locationId={locationId} metrics={metrics} />
        )}
      </Box>
    </Box>
  );
};

export default DashboardDataEntry;
